import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, Loader2, Home, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BookingSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paypalOrderId = searchParams.get('paypal_order_id') || searchParams.get('token');
  const bookingIdFromUrl = searchParams.get('booking_id');
  const bookingIdFromState = location.state?.bookingId;
  const bookingId = bookingIdFromUrl || bookingIdFromState;
  const paymentOptionFromState = location.state?.paymentOption;
  const [status, setStatus] = useState('checking');
  const [bookingInfo, setBookingInfo] = useState(null);
  const [paymentOption, setPaymentOption] = useState(paymentOptionFromState || null);

  // Clear pendingBookingId from localStorage on success page load
  useEffect(() => {
    return () => {
      // Only clear on unmount if payment succeeded
      if (status === 'success') {
        localStorage.removeItem('pendingBookingId');
      }
    };
  }, [status]);

  useEffect(() => {
    if (paymentOptionFromState === 'pay_on_arrival' && bookingId) {
      // Pay on arrival — no payment to verify, just fetch booking details
      fetchAndShowBooking(bookingId, true);
    } else if (paypalOrderId) {
      capturePayPalPayment();
    } else if (sessionId) {
      pollPaymentStatus();
    } else if (bookingId) {
      // Nexi flow — trigger verification then poll for result
      verifyNexiPayment();
    } else {
      navigate('/');
    }
  }, [sessionId, bookingId, paypalOrderId]);

  const fetchAndShowBooking = async (id, forceSuccess = false) => {
    try {
      const response = await axios.get(`${API}/bookings/${id}`);
      const booking = response.data;
      setBookingInfo(booking);
      setPaymentOption(booking.payment_option);

      if (forceSuccess || booking.payment_status === 'paid' || booking.payment_status === 'deposit_paid') {
        setStatus('success');
        localStorage.removeItem('pendingBookingId');
      } else if (booking.payment_status === 'failed') {
        setStatus('failed');
      } else {
        // Still pending — return false so caller can continue polling
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error fetching booking:', error);
      return false;
    }
  };

  const capturePayPalPayment = async () => {
    try {
      const captureResponse = await axios.post(`${API}/paypal/capture/${paypalOrderId}`);
      if (captureResponse.data.status === 'success') {
        const bid = captureResponse.data.booking_id;
        if (bid) {
          await fetchAndShowBooking(bid, true);
        } else {
          setStatus('success');
          localStorage.removeItem('pendingBookingId');
          toast.success('Payment successful!');
        }
      } else {
        setStatus('failed');
        toast.error('Payment capture failed');
      }
    } catch (error) {
      console.error('Error capturing PayPal payment:', error);
      setStatus('failed');
      toast.error(error.response?.data?.detail || 'Failed to process PayPal payment');
    }
  };

  const verifyNexiPayment = async () => {
    try {
      // Trigger backend Nexi verification (which calls Nexi API to check status)
      await axios.get(`${API}/nexi-payment-success?booking_id=${bookingId}`);
    } catch (error) {
      console.error('Error triggering Nexi verification:', error);
    }
    // Regardless of verification result, poll the booking status
    pollBookingStatus(bookingId);
  };

  const pollBookingStatus = async (id, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      // After max attempts, show whatever state we have
      const resolved = await fetchAndShowBooking(id);
      if (!resolved) {
        // Still pending after all attempts — show pending state, not false success
        setStatus('pending');
      }
      return;
    }

    const resolved = await fetchAndShowBooking(id);
    if (!resolved) {
      setTimeout(() => pollBookingStatus(id, attempts + 1), pollInterval);
    }
  };

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('pending');
      return;
    }

    try {
      const response = await axios.get(`${API}/bookings/status/${sessionId}`);
      if (response.data.payment_status === 'paid' || response.data.payment_status === 'deposit_paid') {
        setStatus('success');
        setBookingInfo(response.data);
        setPaymentOption(response.data.payment_option);
        localStorage.removeItem('pendingBookingId');
        toast.success('Payment successful! Your booking is confirmed.');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        toast.error('Payment session expired.');
        return;
      }
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4">
      <Card className="max-w-md w-full shadow-xl border-0" data-testid="booking-success-card">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Verifying Payment</h2>
              <p className="text-gray-600">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-green-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="success-message">
                Booking Confirmed!
              </h2>
              
              {paymentOption === 'pay_on_arrival' ? (
                <>
                  <p className="text-gray-600 mb-4">
                    Your reservation has been confirmed. Payment will be collected upon arrival.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Booking ID:</strong> {bookingInfo?.id}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Guest:</strong> {bookingInfo?.guest_name}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Email:</strong> {bookingInfo?.guest_email}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Check-in:</strong> {bookingInfo?.check_in}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Check-out:</strong> {bookingInfo?.check_out}
                    </p>
                    <p className="text-sm text-gray-700 font-semibold">
                      <strong>Amount Due on Arrival:</strong> €{bookingInfo?.total_amount?.toFixed(2)}
                    </p>
                  </div>
                  <p className="text-gray-600 mb-6 text-sm">
                    A confirmation email has been sent to <strong>{bookingInfo?.guest_email}</strong>
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-4">
                    Your payment was successful and your booking is confirmed.
                  </p>
                  {bookingInfo && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Booking ID:</strong> {bookingInfo?.id}
                      </p>
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Guest:</strong> {bookingInfo?.guest_name}
                      </p>
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Check-in:</strong> {bookingInfo?.check_in}
                      </p>
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Check-out:</strong> {bookingInfo?.check_out}
                      </p>
                      {bookingInfo?.deposit_amount > 0 && (
                        <p className="text-sm text-green-700">
                          <strong>Deposit Paid:</strong> €{bookingInfo?.deposit_amount?.toFixed(2)}
                        </p>
                      )}
                      {bookingInfo?.remaining_amount > 0 && (
                        <p className="text-sm text-orange-700 font-semibold">
                          <strong>Remaining Balance (due at check-in):</strong> €{bookingInfo?.remaining_amount?.toFixed(2)}
                        </p>
                      )}
                    </div>
                  )}
                  <p className="text-gray-600 mb-6 text-sm">
                    A confirmation email has been sent to your email address.
                  </p>
                </>
              )}
              
              <Button 
                onClick={() => navigate('/')} 
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                data-testid="back-home-btn"
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-yellow-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Payment Processing
              </h2>
              <p className="text-gray-600 mb-4">
                Your payment is still being processed. You will receive a confirmation email once it's complete.
              </p>
              {bookingInfo && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Booking ID:</strong> {bookingInfo?.id}
                  </p>
                  <p className="text-sm text-gray-700">
                    Please save this ID for your records. If you don't receive a confirmation within 15 minutes, please contact us.
                  </p>
                </div>
              )}
              <Button 
                onClick={() => navigate('/')} 
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                data-testid="back-home-btn"
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 text-3xl">✕</span>
              </div>
              <h2 className="text-2xl font-bold mb-2 text-red-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Payment Failed
              </h2>
              <p className="text-gray-600 mb-6">
                We couldn't verify your payment. Please try again or contact support.
              </p>
              <Button 
                onClick={() => navigate('/')} 
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingSuccess;
